import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/cn';
import { Check } from 'lucide-react';

export type PlanCode = 'trial' | 'essencial' | 'profissional';

export type PlanOption = {
  code: PlanCode;
  name: string;
  price: number | null;
  period: string;
  description: string;
  features: string[];
  highlighted?: boolean;
};

export const SIGNUP_PLAN_OPTIONS: PlanOption[] = [
  {
    code: 'trial',
    name: 'Trial',
    price: 0,
    period: '30 dias',
    description: 'Conheça o sistema sem compromisso',
    features: ['1 propriedade', 'Até 100 animais', 'Suporte por e-mail'],
  },
  {
    code: 'essencial',
    name: 'Essencial',
    price: 149,
    period: '/mês',
    description: 'Pequenas propriedades e recria',
    features: ['3 propriedades', 'Animais ilimitados', 'App mobile', 'Suporte prioritário'],
    highlighted: true,
  },
  {
    code: 'profissional',
    name: 'Profissional',
    price: 349,
    period: '/mês',
    description: 'Operação completa com equipe',
    features: [
      'Propriedades ilimitadas',
      'Múltiplos usuários',
      'Importação em massa',
      'Relatórios avançados',
    ],
  },
];

export function PlanPicker({
  value,
  onChange,
  className,
}: {
  value: PlanCode;
  onChange: (code: PlanCode) => void;
  className?: string;
}) {
  return (
    <div className={cn('grid gap-3', className)}>
      {SIGNUP_PLAN_OPTIONS.map((plan) => {
        const selected = value === plan.code;
        return (
          <button
            key={plan.code}
            type="button"
            onClick={() => onChange(plan.code)}
            className={cn(
              'w-full rounded-2xl border p-4 text-left transition-all',
              selected
                ? 'border-manejai-forest bg-manejai-forest/[0.04] ring-2 ring-manejai-forest/30'
                : 'border-manejai-sand/80 bg-white hover:border-manejai-moss/40',
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-display font-semibold text-manejai-forest">{plan.name}</p>
                  {plan.code === 'trial' && <Badge tone="success">Começar grátis</Badge>}
                  {plan.highlighted && <Badge tone="warning">Recomendado</Badge>}
                </div>
                <p className="mt-1 text-xs text-manejai-bark/60">{plan.description}</p>
              </div>
              <div className="shrink-0 text-right">
                <p className="font-display text-xl font-semibold text-manejai-forest">
                  {plan.price === 0 || plan.price == null
                    ? 'Grátis'
                    : `R$ ${Number(plan.price).toFixed(0)}`}
                </p>
                <p className="text-xs text-manejai-bark/55">{plan.period}</p>
              </div>
            </div>
            <ul className="mt-3 space-y-1.5">
              {plan.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-manejai-bark/75">
                  <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-manejai-sage" />
                  {f}
                </li>
              ))}
            </ul>
          </button>
        );
      })}
    </div>
  );
}
